import React, { useState } from 'react';
import axios from 'axios';
import styled, { keyframes, css } from 'styled-components';
import { useForm } from 'react-hook-form';
import {
  FiArrowRight, FiMail, FiMapPin,
  FiCheckCircle, FiAlertCircle, FiUser,
  FiCalendar, FiMessageSquare, FiHome,
  FiHeart, FiDollarSign, FiBriefcase,
  FiFlag, FiUsers, FiClock,
  FiPhone
} from 'react-icons/fi';
// import { Link } from 'react-router-dom';
import Button from '../shared/Button';

// Animations
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

// Design Tokens
const colors = {
  primary: '#2563EB',
  primaryDark: '#1D4ED8',
  primaryLight: '#3B82F6',
  secondary: '#10B981',
  error: '#DC2626',
  success: '#10B981',
  warning: '#F59E0B',
  background: '#F8FAFC',
  surface: '#FFFFFF',
  textPrimary: '#1E293B',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  overlay: 'rgba(0, 0, 0, 0.5)'
};

const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px'
};

const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
  full: '9999px'
};

const typography = {
  heading1: '2.5rem',
  heading2: '2rem',
  heading3: '1.5rem',
  heading4: '1.25rem',
  body: '1rem',
  small: '0.875rem',
  tiny: '0.75rem'
};

const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
};

const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px'
};

// Layout Components
const PageContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: ${spacing.xxxl};
  background: linear-gradient(to bottom right, ${colors.background}, white);
  
  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing.xl};
  }
`;

const Section = styled.section`
  background: ${colors.surface};
  border-radius: ${borderRadius.xxl};
  overflow: hidden;
  box-shadow: ${shadows.md};
  margin-bottom: ${spacing.xxxl};
`;

const Card = styled.div`
  background: ${colors.surface};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  box-shadow: ${shadows.sm};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${shadows.md};
    transform: translateY(-2px);
  }
`;


const Subheading = styled.h3`
  font-size: ${typography.heading3};
  color: ${colors.textPrimary};
  font-weight: 600;
  margin-bottom: ${spacing.md};
  line-height: 1.4;
`;

const Text = styled.p`
  font-size: ${typography.body};
  color: ${colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${spacing.md};
`;


const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  
  ${props => props.$hasError && css`
    input, textarea, select {
      border-color: ${colors.error} !important;
      box-shadow: 0 0 0 1px ${colors.error} !important;
    }
  `}
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-size: ${typography.small};
  color: ${colors.textPrimary};
  font-weight: 500;
  
  svg {
    color: ${colors.primary};
  }
  
  span.required {
    color: ${colors.error};
  }
`;

const Input = styled.input`
  padding: ${spacing.md};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.md};
  font-size: ${typography.body};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.primary}20;
  }
  
  &::placeholder {
    color: ${colors.textSecondary};
    opacity: 0.6;
  }
`;

const Textarea = styled.textarea`
  padding: ${spacing.md};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.md};
  font-size: ${typography.body};
  min-height: 120px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.primary}20;
  }
`;

const Select = styled.select`
  padding: ${spacing.md};
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.md};
  font-size: ${typography.body};
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${spacing.md} center;
  background-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px ${colors.primary}20;
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  color: ${colors.error};
  font-size: ${typography.small};
  margin-top: ${spacing.xs};
  
  svg {
    flex-shrink: 0;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: ${spin} 1s linear infinite;
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  
  ${props => props.$isOpen && css`
    opacity: 1;
    visibility: visible;
  `}
`;

const DialogContent = styled.div`
  background: ${colors.surface};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  max-width: 480px;
  width: 90%;
  text-align: center;
  box-shadow: ${shadows.xl};
  transform: scale(0.95);
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.2s ease forwards;
  
  ${DialogOverlay}:hover & {
    transform: scale(1);
  }
`;

const DialogIcon = styled.div`
  color: ${colors.success};
  font-size: 48px;
  margin-bottom: ${spacing.lg};
  animation: ${float} 3s ease-in-out infinite;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spacing.md};
  margin-top: ${spacing.xl};
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  position: relative;

  @media (max-width: 992px) {
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin-bottom: 50px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #151c48;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #5ab9d3, #151c48);
    border-radius: 2px;
  }

  @media (max-width: 992px) {
    font-size: 2.4rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: ${spacing.xxl};
  padding-bottom: ${spacing.xl};
  border-bottom: 1px solid ${colors.border};
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const FormSectionTitle = styled.h4`
  font-size: ${typography.heading4};
  color: ${colors.textPrimary};
  margin-bottom: ${spacing.lg};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  
  svg {
    color: ${colors.primary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.lg};
  
  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

// const MultiSelect = styled.select`
//   padding: ${spacing.md};
//   border: 1px solid ${colors.border};
//   border-radius: ${borderRadius.md};
//   font-size: ${typography.body};
//   appearance: none;
//   background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
//   background-repeat: no-repeat;
//   background-position: right ${spacing.md} center;
//   background-size: 16px;
//   min-height: 120px;
//   resize: vertical;

//   &:focus {
//     outline: none;
//     border-color: ${colors.primary};
//     box-shadow: 0 0 0 3px ${colors.primary}20;
//   }

//   option {
//     padding: ${spacing.sm};
//     margin: ${spacing.xs} 0;
//   }
// `;

const SelectedServices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  margin-top: ${spacing.sm};
`;

const ServiceTag = styled.span`
  background-color: ${colors.primaryLight};
  color: white;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borderRadius.full};
  font-size: ${typography.small};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;



// Data
const childrenOptions = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: 'none', label: 'Não possuo filhos' },
  { value: 'other', label: 'Outro' }
];

const petTypeOptions = [
  { value: 'dog', label: 'Cão' },
  { value: 'cat', label: 'Gato' },
  { value: 'none', label: 'Não' },
  { value: 'other', label: 'Outro' }
];

const petSizeOptions = [
  { value: 'small', label: 'Pequeno' },
  { value: 'medium', label: 'Médio' },
  { value: 'large', label: 'Grande' },
  { value: 'none', label: 'Não se aplica' }
];

const serviceTypeOptions = [
  { value: 'property-purchase', label: 'Auxílio compra imóvel' },
  { value: 'rental', label: 'Arrendamento' },
  { value: 'renovation', label: 'Reforma' },
  { value: 'decoration', label: 'Decoração' },
  { value: 'financial-planning', label: 'Planejamento financeiro' },
  { value: 'visa-support', label: 'Suporte vistos' },
  { value: 'other', label: 'Outro' }
];

const propertyTypeOptions = [
  { value: 'T0', label: 'T0' },
  { value: 'T1', label: 'T1' },
  { value: 'T2', label: 'T2' },
  { value: 'T3+', label: 'T3+' },
  { value: 'office', label: 'Escritório' },
  { value: 'commercial', label: 'Sala comercial' },
  { value: 'other', label: 'Outro' }
];

const planningTimeOptions = [
  { value: '1', label: '1 ano' },
  { value: '2', label: '2 anos' },
  { value: '3', label: '3 anos' },
  { value: '4', label: '4 anos' },
  { value: '5+', label: '5+ anos' }
];

const movingReasonOptions = [
  { value: 'security', label: 'Segurança' },
  { value: 'quality-of-life', label: 'Qualidade de vida' },
  { value: 'studies', label: 'Estudos' },
  { value: 'work', label: 'Trabalho' },
  { value: 'other', label: 'Outro' }
];

const brazilianStates = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

const RelocationEmailForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    watch,
    setValue,
    reset
  } = useForm({ mode: 'onChange' });

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const watchedValues = watch();
  const needsPetSize = watchedValues.petType && watchedValues.petType !== 'none';
  const showCustomMovingReason = watchedValues.movingReason === 'other';

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8087";
    return `${baseUrl.replace(/\/+$/, "")}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  };

  const submitRelocationLead = async (formData) => {
    try {
      // Construir payload conforme estrutura da API
      const payload = {
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || '',
        region: formData.targetCity.trim(),
        observation: [
          `Profissão: ${formData.profession}`,
          `Nacionalidade: ${formData.nationality}`,
          formData.spouseName && `Cônjuge: ${formData.spouseName}`,
          formData.spouseProfession && `Profissão Cônjuge: ${formData.spouseProfession}`,
          `Filhos: ${formData.numberOfChildren}`,
          `Estado Residência: ${formData.currentState}`,
          `Tipo Serviço: ${formData.serviceType}`,
          `Tipo Imóvel: ${formData.propertyType}`,
          formData.maxPurchaseValue && `Valor Máx. Compra: €${formData.maxPurchaseValue}`,
          formData.maxRentValue && `Valor Máx. Aluguel: €${formData.maxRentValue}`,
          `Tempo Planejamento: ${formData.planningTime}`,
          `Motivo Mudança: ${formData.movingReason}`,
          formData.customMovingReason && `Detalhe Motivo: ${formData.customMovingReason}`,
          formData.petType && `Tipo Pet: ${formData.petType}`,
          formData.petSize && `Porte Pet: ${formData.petSize}`,
          formData.financialPlan && `Plano Financeiro: ${formData.financialPlan}`
        ].filter(Boolean).join('\n'),
        relocationDate: formData.arrivalDate || null,
        profession: formData.profession,
        spouseName: formData.spouseName || null,
        spouseProfession: formData.spouseProfession || null,
        numberOfChildren: formData.numberOfChildren,
        nationality: formData.nationality,
        currentState: formData.currentState,
        arrivalDate: formData.arrivalDate || null,
        targetCity: formData.targetCity,
        petType: formData.petType || null,
        petSize: formData.petSize || null,
        serviceType: formData.serviceType,
        propertyType: formData.propertyType,
        maxPurchaseValue: formData.maxPurchaseValue ?
          parseFloat(formData.maxPurchaseValue) : null,
        maxRentValue: formData.maxRentValue ?
          parseFloat(formData.maxRentValue) : null,
        planningTime: formData.planningTime,
        financialPlan: formData.financialPlan || null,
        movingReason: formData.movingReason,
      };

      // Fazer a chamada à API sem token de autenticação
      const response = await axios.post(
        buildApiUrl('relocation-leads'),
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status !== 204) {
        throw new Error('Erro ao enviar formulário de realocação');
      }

      return {
        success: true,
        message: 'Formulário enviado com sucesso!'
      };

    } catch (error) {
      console.error('Erro ao enviar lead de realocação:', error);

      let errorMessage = 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.';

      if (axios.isAxiosError(error)) {
        // Tratar erros específicos da API
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Dados inválidos. Verifique os campos do formulário.';
        } else if (error.response?.status === 500) {
          errorMessage = 'Erro no servidor. Por favor, tente novamente mais tarde.';
        }
      }

      throw new Error(errorMessage);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setSubmitError('');

    try {
      const result = await submitRelocationLead(data);
      setSuccessMessage(result.message);
      setIsSuccessDialogOpen(true);
      reset();
    } catch (error) {
      setSubmitError(error.message);
      console.error('Submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  // const onSubmit = async (data) => {
  //   setLoading(true);
  //   setSubmitError('');

  //   try {
  //     const result = await submitRelocationLead(data);
  //     setSuccessMessage(result.message);
  //     setIsSuccessDialogOpen(true);
  //     reset();
  //   } catch (error) {
  //     setSubmitError(error.message);
  //     console.error('Submission error:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <PageContainer>
      <Section>
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Formulário de Realocação para Portugal</SectionTitle>
            <Text>
              Preencha os dados abaixo para recebermos sua solicitação de auxílio para mudança para Portugal
            </Text>
          </SectionHeader>
        </SectionContainer>

        <Card>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {submitError && (
              <div style={{
                background: '#FEE2E2',
                color: colors.error,
                padding: spacing.md,
                borderRadius: borderRadius.md,
                marginBottom: spacing.lg,
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm
              }}>
                <FiAlertCircle size={18} />
                {submitError}
              </div>
            )}

            {/* Informações Pessoais */}
            <FormSection>
              <FormSectionTitle>
                <FiUser size={20} />
                Informações Pessoais
              </FormSectionTitle>

              <Grid>
                <FormGroup $hasError={!!errors.email}>
                  <Label>
                    <FiMail size={16} />
                    Email <span className="required">*</span>
                  </Label>
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    {...register('email', {
                      required: 'Email é obrigatório',
                      pattern: {
                        value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                        message: 'Por favor, insira um email válido'
                      }
                    })}
                  />
                  {errors.email && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.email.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.fullName}>
                  <Label>
                    <FiUser size={16} />
                    Nome Completo <span className="required">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Seu nome completo"
                    {...register('fullName', {
                      required: 'Nome é obrigatório',
                      minLength: {
                        value: 3,
                        message: 'Nome deve ter pelo menos 3 caracteres'
                      }
                    })}
                  />
                  {errors.fullName && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.fullName.message}
                    </ErrorMessage>
                  )}
                </FormGroup>
                <FormGroup $hasError={!!errors.phone}>
                  <Label>
                    <FiPhone size={16} />
                    Telefone <span className="required">*</span>
                  </Label>
                  <Input
                    type="tel"
                    placeholder="(XX) XXXXX-XXXX"
                    {...register('phone', {
                      required: 'Telefone é obrigatório',
                      pattern: {
                        value: /^\(?\d{2}\)?[\s-]?\d{4,5}[\s-]?\d{4}$/,
                        message: 'Por favor, insira um telefone válido'
                      }
                    })}
                  />
                  {errors.phone && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.phone.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.profession}>
                  <Label>
                    <FiBriefcase size={16} />
                    Profissão <span className="required">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Sua profissão"
                    {...register('profession', {
                      required: 'Profissão é obrigatória'
                    })}
                  />
                  {errors.profession && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.profession.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.nationality}>
                  <Label>
                    <FiFlag size={16} />
                    Nacionalidade <span className="required">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Sua nacionalidade"
                    {...register('nationality', {
                      required: 'Nacionalidade é obrigatória'
                    })}
                  />
                  {errors.nationality && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.nationality.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiUsers size={16} />
                    Nome da Esposa(o)
                  </Label>
                  <Input
                    type="text"
                    placeholder="Nome da esposa(o) (opcional)"
                    {...register('spouseName')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiBriefcase size={16} />
                    Profissão da Esposa(o)
                  </Label>
                  <Input
                    type="text"
                    placeholder="Profissão da esposa(o) (opcional)"
                    {...register('spouseProfession')}
                  />
                </FormGroup>

                <FormGroup $hasError={!!errors.numberOfChildren}>
                  <Label>
                    <FiUsers size={16} />
                    Quantos filhos possuem? <span className="required">*</span>
                  </Label>
                  <Select
                    {...register('numberOfChildren', {
                      required: 'Este campo é obrigatório'
                    })}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione uma opção</option>
                    {childrenOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {errors.numberOfChildren && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.numberOfChildren.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.currentState}>
                  <Label>
                    <FiMapPin size={16} />
                    Estado de Residência <span className="required">*</span>
                  </Label>
                  <Select
                    {...register('currentState', {
                      required: 'Estado é obrigatório'
                    })}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione seu estado</option>
                    {brazilianStates.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Select>
                  {errors.currentState && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.currentState.message}
                    </ErrorMessage>
                  )}
                </FormGroup>
              </Grid>
            </FormSection>

            {/* Planos de Mudança */}
            <FormSection>
              <FormSectionTitle>
                <FiCalendar size={20} />
                Planos de Mudança
              </FormSectionTitle>

              <Grid>
                <FormGroup $hasError={!!errors.arrivalDate}>
                  <Label>
                    <FiCalendar size={16} />
                    Data Prevista para Chegada <span className="required">*</span>
                  </Label>
                  <Input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    {...register('arrivalDate', {
                      required: 'Data de chegada é obrigatória',
                      validate: (value) => {
                        const selectedDate = new Date(value);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return selectedDate >= today || 'A data deve ser futura ou atual';
                      }
                    })}
                  />
                  {errors.arrivalDate && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.arrivalDate.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.targetCity}>
                  <Label>
                    <FiMapPin size={16} />
                    Cidade onde Pretende Morar <span className="required">*</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="Cidade em Portugal"
                    {...register('targetCity', {
                      required: 'Cidade é obrigatória'
                    })}
                  />
                  {errors.targetCity && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.targetCity.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.planningTime}>
                  <Label>
                    <FiClock size={16} />
                    Tempo de Planejamento para Mudança <span className="required">*</span>
                  </Label>
                  <Select
                    {...register('planningTime', {
                      required: 'Tempo de planejamento é obrigatório'
                    })}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione o tempo</option>
                    {planningTimeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {errors.planningTime && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.planningTime.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup $hasError={!!errors.movingReason}>
                  <Label>
                    <FiArrowRight size={16} />
                    Motivo para Mudança <span className="required">*</span>
                  </Label>
                  <Select
                    {...register('movingReason', {
                      required: 'Motivo é obrigatório'
                    })}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione o motivo</option>
                    {movingReasonOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {errors.movingReason && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.movingReason.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                {showCustomMovingReason && (
                  <FormGroup>
                    <Label>
                      <FiMessageSquare size={16} />
                      Especifique o Motivo
                    </Label>
                    <Input
                      type="text"
                      placeholder="Descreva seu motivo"
                      {...register('customMovingReason')}
                    />
                  </FormGroup>
                )}
              </Grid>
            </FormSection>

            {/* Informações sobre Pets */}
            <FormSection>
              <FormSectionTitle>
                <FiHeart size={20} />
                Informações sobre Pets
              </FormSectionTitle>

              <Grid>
                <FormGroup>
                  <Label>
                    <FiHeart size={16} />
                    Pet que Irá Trazer
                  </Label>
                  <Select
                    {...register('petType')}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione uma opção</option>
                    {petTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                {needsPetSize && (
                  <FormGroup>
                    <Label>
                      <FiHeart size={16} />
                      Porte do Pet
                    </Label>
                    <Select
                      {...register('petSize')}
                      defaultValue=""
                    >
                      <option value="" disabled>Selecione o porte</option>
                      {petSizeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                )}
              </Grid>
            </FormSection>

            {/* Serviços e Preferências de Imóvel */}
            <FormSection>
              <FormSectionTitle>
                <FiHome size={20} />
                Serviços e Preferências de Imóvel
              </FormSectionTitle>

              <Grid>
                <FormGroup $hasError={!!errors.serviceType}>
                  <Label>
                    <FiBriefcase size={16} />
                    Tipo de Serviço Desejado <span className="required">*</span>
                  </Label>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                    {serviceTypeOptions.map((option) => (
                      <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                        <input
                          type="checkbox"
                          value={option.value}
                          checked={watch('serviceType')?.includes(option.value) || false}
                          onChange={(e) => {
                            const currentValues = watch('serviceType') || [];
                            if (e.target.checked) {
                              setValue('serviceType', [...currentValues, option.value], { shouldValidate: true });
                            } else {
                              setValue(
                                'serviceType',
                                currentValues.filter((v) => v !== option.value),
                                { shouldValidate: true }
                              );
                            }
                          }}
                          style={{ width: '16px', height: '16px' }}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>

                  {watch('serviceType')?.length > 0 && (
                    <SelectedServices>
                      {watch('serviceType').map((service) => (
                        <ServiceTag key={service}>
                          {serviceTypeOptions.find((opt) => opt.value === service)?.label}
                        </ServiceTag>
                      ))}
                    </SelectedServices>
                  )}

                  {errors.serviceType && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.serviceType.message}
                    </ErrorMessage>
                  )}
                </FormGroup>


                <FormGroup $hasError={!!errors.propertyType}>
                  <Label>
                    <FiHome size={16} />
                    Tipo de Imóvel Desejado <span className="required">*</span>
                  </Label>
                  <Select
                    {...register('propertyType', {
                      required: 'Tipo de imóvel é obrigatório'
                    })}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione o tipo</option>
                    {propertyTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {errors.propertyType && (
                    <ErrorMessage>
                      <FiAlertCircle size={14} /> {errors.propertyType.message}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiDollarSign size={16} />
                    Valor Máximo para Compra do Imóvel (€)
                  </Label>
                  <Input
                    type="number"
                    placeholder="Ex: 300000"
                    {...register('maxPurchaseValue')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiDollarSign size={16} />
                    Valor Máximo para Arrendamento (€)
                  </Label>
                  <Input
                    type="number"
                    placeholder="Ex: 1500"
                    {...register('maxRentValue')}
                  />
                </FormGroup>
              </Grid>
            </FormSection>

            {/* Planejamento Financeiro */}
            <FormSection>
              <FormSectionTitle>
                <FiDollarSign size={20} />
                Planejamento Financeiro
              </FormSectionTitle>

              <FormGroup>
                <Label>
                  <FiMessageSquare size={16} />
                  Plano Financeiro para Mudança
                </Label>
                <Textarea
                  placeholder="Descreva brevemente seu planejamento financeiro para a mudança..."
                  {...register('financialPlan')}
                />
              </FormGroup>
            </FormSection>

            <Button
              type="submit"
              primary
              disabled={loading || !isDirty || !isValid}
            >
              {loading ? (
                <>
                  Enviando... <Spinner />
                </>
              ) : (
                <>
                  Enviar Formulário de Realocação <FiArrowRight />
                </>
              )}
            </Button>
          </Form>
        </Card>
      </Section>

      {/* Success Dialog */}
      <DialogOverlay
        $isOpen={isSuccessDialogOpen}
        onClick={() => setIsSuccessDialogOpen(false)}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogIcon>
            <FiCheckCircle size={48} />
          </DialogIcon>
          <Subheading>{successMessage}</Subheading>
          <Text>
            Recebemos sua solicitação de realocação. Nossa equipe entrará em contato em até 24 horas.
          </Text>
          <Text>
            Enviamos uma confirmação para o e-mail cadastrado com todos os detalhes.
          </Text>
          <DialogActions>
            <Button onClick={() => setIsSuccessDialogOpen(false)}>
              Entendido
            </Button>
          </DialogActions>
        </DialogContent>
      </DialogOverlay>
    </PageContainer>
  );
};

export default RelocationEmailForm;